import ky, { ResponsePromise } from "ky";
import { env } from "@p81-common/shell-core-js";
import { ApplicationName } from "types/CPIPAppIntegration";
interface PartnerSSO {
  token: string;
  tenantId: string;
}
export interface PartnerCheck {
  tenantExists: boolean;
  userExists: boolean;
  checkpointLicenseExists: boolean;
  chargebeeLicenseExists: boolean;
  checkpointLicenseErrors?: number;
  manuallyActivatingLicense?: boolean;
  isCreatingNewWorkspace?: boolean;
  error?: string;
  hasAccess: boolean;
  isDirectAccess?: boolean;
}

export interface CreateUser {
  tenantId: string;
  token: string;
}

export enum SubscriptionStatus {
  PENDING = "pending",
  FAIL = "fail",
  SUCCESS = "success",
}

interface HeartbeatPayload {
  sessionId: string;
}
export interface ValidateTenantSKUPayload {
  appList: ApplicationName[];
}

export type ValidateTenantSKUs = {
  [key in ApplicationName]: boolean;
};

export interface AppSKUsEvalResponse {
  noEval: boolean;
  isPureEval: boolean;
}

export function validateTenantSKU(
  body: ValidateTenantSKUPayload
): Promise<{ checkRes: boolean[] }> {
  return ky
    .post(
      new Request(
        `${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/tenant/skusVerification`,
        {
          method: "post",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      )
    )
    .json();
}

export function getAppSKUsEval(
  app: ApplicationName
): Promise<AppSKUsEvalResponse> {
  return ky
    .post(
      new Request(
        `${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/tenant/app/skus/eval`,
        {
          method: "post",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ app }),
        }
      )
    )
    .json();
}

export function heartbeat(body: HeartbeatPayload): void {
  ky.post(
    new Request(`${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/heartbeat`, {
      method: "post",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })
  ).json();
}

export function partnerCheck(): Promise<PartnerCheck> {
  return ky
    .get(`${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/tenant/status`, {
      credentials: "include",
      timeout: 30000,
    })
    .json();
}

export function partnerSSO(): Promise<PartnerSSO> {
  return ky
    .get(`${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/sso`, {
      credentials: "include",
      timeout: 30000,
    })
    .json();
}

export function createUser(body: string): Promise<CreateUser> {
  return ky
    .post(
      new Request(`${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/users`, {
        method: "post",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body,
      })
    )
    .json();
}

export function createWorkspace(body: {
  password: string;
  tenantId: string;
}): Promise<void> {
  return ky
    .post(`${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/tenant`, {
      credentials: "include",
      json: body,
      timeout: 30000,
    })
    .json();
}

export function checkSubscriptionStatus(): Promise<{
  status: SubscriptionStatus;
}> {
  return ky
    .get(
      new Request(
        `${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/subscription/status`,
        { credentials: "include" }
      ),
      {
        retry: 0,
      }
    )
    .json();
}

export function syncUserRole(): Promise<void> {
  return ky
    .post(
      new Request(`${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/user/role/sync`, {
        method: "post",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      })
    )
    .json();
}

export function triggerTenantEvent(appName: string): ResponsePromise {
  return ky.post(
    new Request(
      `${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/tenant/event`,
      {
        method: "post",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ appName }),
      }
    ),
    {
      retry: 0,
    }
  );
}

// ── GenAI Protect integration ──
// Types for the CloudInfra /api/v1/app response used to discover
// the GenAI Protect bundle URL and deployment config.

interface CIAppPage {
  displayName: string;
  name: string;
  componentUrl: string | null;
  subPages1?: CIAppPage[];
}

interface CIAppDeployment {
  pages: CIAppPage[];
  configMap?: Record<string, string>;
}

interface CIApp {
  displayName: string;
  name: string;
  deployment: CIAppDeployment;
}

interface CIAppsResponse {
  success: boolean;
  apps: CIApp[];
}

export interface GenAiAppInfo {
  bundleUrl: string;
  configMap: Record<string, string>;
}

// Recursively find first non-null componentUrl in a page tree
function findBundleUrl(pages: CIAppPage[]): string | null {
  for (const page of pages) {
    if (page.componentUrl) return page.componentUrl;
    if (page.subPages1) {
      const url = findBundleUrl(page.subPages1);
      if (url) return url;
    }
  }
  return null;
}

/** SPA-lifetime cache for GET /api/v1/app so multiple iframe fetchers
 *  (GenAi, BrowseV2, future) share one roundtrip. The promise itself is
 *  cached so concurrent callers also dedupe. Cleared on failure so a
 *  subsequent caller can retry. */
let ciAppsCache: Promise<CIAppsResponse | null> | null = null;

export function clearCIAppsCache(): void {
  ciAppsCache = null;
}

async function getCIApps(): Promise<CIAppsResponse | null> {
  if (ciAppsCache) return ciAppsCache;
  const baseUrl = env.REACT_APP_CLOUDINFRA_BASE_URL;
  if (!baseUrl) return null;

  ciAppsCache = (async () => {
    try {
      const response = await ky
        .get(`${baseUrl}/api/v1/app`, { credentials: "include", retry: 0 })
        .json<CIAppsResponse>();
      if (!response.success) {
        ciAppsCache = null;
        return null;
      }
      return response;
    } catch {
      ciAppsCache = null;
      return null;
    }
  })();

  return ciAppsCache;
}

function extractAppInfo(
  apps: CIApp[] | undefined,
  match: (app: CIApp) => boolean
): GenAiAppInfo | null {
  const app = apps?.find(match);
  if (!app) return null;
  const bundleUrl = findBundleUrl(app.deployment?.pages ?? []);
  if (!bundleUrl) return null;
  return { bundleUrl, configMap: app.deployment?.configMap ?? {} };
}

/**
 * Identifies the GenAI Protect (Workforce AI Security) app in the CloudInfra
 * catalog. Matches on the stable `name` only — NOT `displayName`.
 *
 * A previous version also accepted any app whose displayName contained
 * "workforce ai". When a CloudInfra-side change gave the staging
 * `GenAIBrowseCore` app a "workforce ai" displayName, that loose fallback
 * matched it (it is ordered before the real app), so `Array.find()` returned
 * the wrong app — one with no
 * bundle URL — and the Gen AI DLP page rendered "Error" (P81-135014). `name`
 * is the immutable app identifier; `displayName` is a mutable, human-entered
 * label, so it must never drive selection.
 */
export function isGenAiProtectApp(app: Pick<CIApp, "name">): boolean {
  return app.name === "GenAIProtect";
}

/**
 * Fetch the GenAI Protect app info from the CloudInfra gateway.
 *
 * Returns null if REACT_APP_CLOUDINFRA_BASE_URL is not configured,
 * the API call fails, or the GenAI app is not registered.
 */
export async function getGenAiAppInfo(): Promise<GenAiAppInfo | null> {
  const response = await getCIApps();
  if (!response) return null;
  return extractAppInfo(response.apps, isGenAiProtectApp);
}

export interface BrowseV2AppInfo {
  bundleUrl: string;
  configMap: Record<string, string>;
}

/** Returns the BrowseV2 (Enterprise Browser) bundle URL and configMap from
 *  GET /api/v1/app, or null if the base URL is unset, the call fails, or
 *  the app isn't registered. App name match is case-sensitive. */
export async function getBrowseV2AppInfo(): Promise<BrowseV2AppInfo | null> {
  const response = await getCIApps();
  if (!response) return null;
  return extractAppInfo(response.apps, (app) => app.name === "BrowseV2");
}

/**
 * Fetch the current user's full profile from the CloudInfra gateway.
 *
 * Returns the portal user object with roles, permissions, featureFlags,
 * and tenant information. This data is needed by the GenAI Protect bundle
 * to determine admin vs read-only mode (via roles.global).
 *
 * Uses the cloudinfra session cookie for authentication.
 */
export async function getPortalUserProfile(): Promise<Record<string, unknown> | null> {
  const baseUrl = env.REACT_APP_CLOUDINFRA_BASE_URL;
  if (!baseUrl) return null;

  const response: { success: boolean; user: Record<string, unknown> } = await ky
    .get(`${baseUrl}/api/v1/user/profile`, {
      credentials: "include",
      retry: 0,
    })
    .json();

  return response.success ? response.user : null;
}

export interface FirstTimeTokenResponse {
  success: boolean;
  firstTimeToken?: string;
}

export interface SaasMenuPagesResponse {
  pages: Array<{ display_name: string; url: string }>;
}

export function getFirstTimeToken(
  email: string
): Promise<FirstTimeTokenResponse> {
  return ky
    .post(`${env.REACT_APP_CP_MIDDLEWARE_BASE_URL}/partner/firstTimeToken`, {
      credentials: "include",
      json: { email },
      retry: 0,
    })
    .json();
}

export function getSaasMenuPages(): Promise<SaasMenuPagesResponse> {
  return ky
    .get(`${env.REACT_APP_CLOUDINFRA_BASE_URL}/app/saasmenu`, {
      credentials: "include",
      retry: 0,
    })
    .json();
}

// ── Explicit SaaS tenant provisioning (AT-8252) ──
// Replaces today's implicit auto-provision on menu read (which happens inside
// the atmosec `noAuthSASE` middleware). SASE now drives provisioning
// synchronously: getTenant → (not exists) → createTenant → getSaasMenuPages.
// Both endpoints go through the CloudInfra gateway, same pattern as
// `/app/saasmenu` → atmosec `/v1/infinity/menu`. The gateway route is the
// single-token `/app/saastenant` (not a nested `/app/saas/tenant`, which the
// gateway can't route) → atmosec `/v1/infinity/tenant`.

export interface GetTenantResponse {
  exists: boolean;
}

// createTenant is an idempotent get-or-create on the atmosec side; AlreadyExists
// is treated as success. Response shape is intentionally loose — Phase 1
// (AT-8250) defines the exact record.
export interface CreateTenantResponse {
  exists?: boolean;
}

export function getTenant(): Promise<GetTenantResponse> {
  return ky
    .get(`${env.REACT_APP_CLOUDINFRA_BASE_URL}/app/saastenant`, {
      credentials: "include",
      retry: 0,
    })
    .json();
}

export function createTenant(): Promise<CreateTenantResponse> {
  return ky
    .post(`${env.REACT_APP_CLOUDINFRA_BASE_URL}/app/saastenant`, {
      credentials: "include",
      retry: 0,
    })
    .json();
}

export async function getDataTypesFromCP(): Promise<ResponsePromise> {
  try {
    return await ky
      .get(
        new Request(
          `${env.REACT_APP_CLOUDINFRA_BASE_URL}/app/dlp/v2/config/720bfa26-b3b5-48aa-ac2e-a4d22cad2bbf/dts/predefined`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        ),
        {
          retry: 0,
          timeout: 90000,
        }
      )
      .json();
  } catch (e) {
    return {
      status: "SUCCESS",
      id: "3507cdbc-0248-40e0-b106-e3ef06db991c",
      dataTypes: [
        {
          id: "408f49a3-5612-4fdc-a3f0-a963a3878f64",
          name: "Source Code",
          type: "GROUP",
          createType: "PRE_DEFINED",
          internal: false,
        },
        {
          id: "2df6f5b6-961f-4230-a023-67add6c44b9b",
          name: "Source Code - ASP",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
        {
          id: "701dcb86-d7dd-468d-97de-f55edc8140b4",
          name: "Source Code - ASP dot NET",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
        {
          id: "218f42ba-fde6-4434-a0d2-766bc56bb09c",
          name: "Source Code - C Sharp",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
        {
          id: "844191d7-0315-46f5-a84d-133c9a8fc2b1",
          name: "Source Code - C or CPP",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
        {
          id: "cdca8f0f-d813-49f4-918f-aea086aa7f6e",
          name: "Source Code - FORTRAN",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
        {
          id: "f7481ba5-997e-4aa9-a62f-05be5c185874",
          name: "Source Code - JAVA",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
        {
          id: "2246f0b5-51fb-403b-8370-ce9856255c3f",
          name: "Source Code - MATLAB",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
        {
          id: "ae3473be-5bdf-4afa-b0ee-85d72feb9104",
          name: "Source Code - Visual Basic",
          type: "WEIGHTED_WORDS",
          createType: "PRE_DEFINED",
          internal: true,
        },
      ],
    };
  }
}
